import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment, createVNode as _createVNode, renderList as _renderList, withModifiers as _withModifiers, createSlots as _createSlots, resolveDirective as _resolveDirective, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-69ffa5d5"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { key: 1 }
const _hoisted_3 = ["title", "innerHTML"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 1 }

import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  PropType,
  Ref,
  ref,
} from 'vue';

import { resume } from '@/plugins/resume';
import { MAX_PER_PAGE } from '@/entities/inspection-group/common';
import { formatDate } from '@/plugins/date-fns';

import { usePaginatedResponse } from '@/utils/query';
import { useGetInspectionGroupsDetailsQuery } from '@/entities/inspection-group/hooks';

import { QueryFilters } from '@/types/services';
import { InspectionGroup } from '@/entities/inspection-group/types';
import { Device, GetDeviceData } from '@/entities/device/types';
import { DeviceGroup, GetDeviceGroupData } from '@/entities/device-group/types';
import { Inspection } from '@/entities/inspection/types';
import InspectionZIPForm from '@/entities/inspection/components/form/InspectionZIPForm.vue';

import {
  NkProfile,
  NkTable,
  NkTableColumn,
  NkGroup,
  NkButton,
  NkInlineModal,
  NkSelect,
  NkOption,
} from '@/core/components';

import NkStatusBadge from '@/entities/status/components/StatusBadge.vue';
import NkColorSwatch from '@/core/components/data-display/ColorSwatch/NkColorSwatch.vue';
import NkInspectionPDFForm from '@/entities/inspection/components/form/InspectionPDFForm.vue';
import NkInspectionPDFDownload from '@/entities/inspection/components/InspectionPDFDownload.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionGroupTable',
  props: {
  showRootDevice: {
    type: Boolean,
    default: true,
  },
  showRootFarm: {
    type: Boolean,
    default: true,
  },
  type: {
    type: String as PropType<typeof inspectionGroupTableType[number]>,
    default: 'default',
  },
  entityData: {
    type: Object as PropType<GetDeviceData | GetDeviceGroupData | undefined>,
    default: undefined,
  },
  itemsPerPageSelection: {
    type: Boolean,
    default: false
  }
},
  setup(__props) {

const props = __props

const inspectionGroupTableType = ['default', 'device', 'device-group'] as const;



const maxPerPage = ref(MAX_PER_PAGE);

const router = useRouter();

const table = ref<HTMLElement>();

const PDFModal = ref();
const PDFModalId: Ref<InspectionGroup['id'] | null> = ref(null);

const CSVModal = ref();
const CSVModalId: Ref<InspectionGroup['id'] | null> = ref(null);

const windowSize = ref<number | null>(null);

const page = ref(1);
const types = ref<string[]>([])

const { t } = useI18n();

const queryFiltersList: any = ref({
  'order[date]': 'desc',
});

const filterChange = (o: any) => {
  if (o.type) {
    if (o.type?.length > 0) {
      queryFiltersList.value.type = o.type
    } else {
      delete queryFiltersList.value.type
    }
  }

  if (o.review) {
    if (o.review?.length > 0) {
      queryFiltersList.value.review = o.review
    } else {
      delete queryFiltersList.value.review
    }
  }
}

const groupsQueryFilters: Ref = computed(() => {
  const filters = {
    ...queryFiltersList.value,
    page: page.value,
    itemsPerPage: maxPerPage.value,
    // eslint-disable-next-line camelcase
  } as QueryFilters<any> & { device_group?: DeviceGroup['id'], device?: Device['id'] };

  if (!props.entityData?.id) {
    return filters;
  }

  if (props.type === 'device-group') {
    filters.device_group = props.entityData?.id;
  }

  if (props.type === 'device') {
    filters.device = props.entityData?.id;
  }

  return filters;
});

const handlePDFModalOpen = (id: InspectionGroup['id']) => {
  PDFModalId.value = id;
  PDFModal.value?.openDialog();
};

const handlePDFModalClose = () => {
  PDFModal.value?.closeDialog();
  PDFModalId.value = null;
};

const handleCSVModalOpen = (id: InspectionGroup['id']) => {
  CSVModalId.value = id;
  CSVModal.value?.openDialog();
};

const handleCSVModalClose = () => {
  CSVModal.value?.closeDialog();
  CSVModalId.value = null;
};

const handleSortChange = (o: any) => {
  const orders: any = { ascending: 'asc', descending: 'desc' };

  if (orders?.[o.order]) {
    queryFiltersList.value[o.prop] = orders[o.order]?.toLowerCase() || '';
  } else {
    delete queryFiltersList.value[o.prop];
  }
};

const { data: inspectionGroupsDetails, isRefetching, isLoading } = useGetInspectionGroupsDetailsQuery({
  refetchOnWindowFocus: false,
}, groupsQueryFilters);

const {data: getGroupsDataValues, meta: getGroupsDataMetas} = usePaginatedResponse(inspectionGroupsDetails);

const getColorFromSeverity = (severityNumber: string) => {
  switch (severityNumber) {
    case '1' :
      return 'info';
    case '2' :
      return 'success';
    case '3' :
      return 'warning';
    case '4' :
      return 'primary';
    case '5' :
      return 'danger';
    case '0' :
      return 'gray';
    default:
      return 'info';
  }
};

const resizeEventCallback = () => {
  if (!table?.value) {
    return;
  }

  windowSize.value = window.screen.width;
};

const handleRowClick = (inspectionGroup: InspectionGroup & { lastInspectionFromFirstDevice: Inspection['id'] }) => {
  if (inspectionGroup.lastInspectionFromFirstDevice) {
    router.push({
      name: 'visionair-inspection',
      params: {
        inspection: inspectionGroup.lastInspectionFromFirstDevice,
      },
      query: {
        view: 2
      }
    });
  }
};

const handlePageChange = (pageNumber: number) => {
  page.value = pageNumber;
  // refetch.value();
};

// const handlePDFdownload = async (id: InspectionGroup['id']) => {
//   if (!isPDFLoading.value.includes(id)) {
//     isPDFLoading.value = [...isPDFLoading.value, id];
//
//     getInspectionGroupReport({
//       id,
//       // ...fields,
//       // devices: uniq(fields.devices.flat()),
//     }).then((pdf) => {
//       window.open(pdf, '_blank');
//     }).catch((e: AxiosError) => {
//       ElNotification({
//         type: 'error',
//         title: (e?.response?.data as string) || t('inspection_group.notification.report_generation.error'),
//         position: 'bottom-right',
//       });
//     }).finally(() => {
//       isPDFLoading.value = isPDFLoading.value.filter(o => o !== id);
//     });
//   }
//   // if (inspectionGroup.id && !idPDFGenerationRunning.value.includes(inspectionGroup.id)) {
//   //   idPDFGenerationRunning.value = [inspectionGroup.id, ...idPDFGenerationRunning.value];
//   //
//   //   await getInspectionGroupReport({
//   //     id: inspectionGroup.id,
//   //   }, {
//   //     severities: inspectionGroup.service?.annotationSeverities,
//   //     types: inspectionGroup.service?.annotationTypes,
//   //   }).then((r) => {
//   //     window.open(r, '_blank');
//   //   }).catch((e: AxiosError) => {
//   //     ElNotification({
//   //       type: 'error',
//   //       title: e?.response?.data?.title || t('inspection_group.notification.report_generation.error'),
//   //       position: 'bottom-right',
//   //     });
//   //   }).finally(() => {
//   //     idPDFGenerationRunning.value = idPDFGenerationRunning.value.filter((id) => id !== inspectionGroup.id);
//   //   });
//   // }
// };

onMounted(() => {
  window.addEventListener('resize', resizeEventCallback);
  window.dispatchEvent(new Event('resize'));
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeEventCallback);
});

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    ref_key: "table",
    ref: table,
    class: "w-full min-w-0"
  }, [
    _createVNode(_unref(NkTable), {
      style: {"width":"100%","min-width":"0","overflow-x":"auto"},
      fit: "",
      data: _unref(getGroupsDataValues),
      clickable: "",
      paginate: "",
      "total-items": _unref(getGroupsDataMetas)?.totalItems || 10,
      "max-per-page": maxPerPage.value,
      "current-page": page.value,
      onRowClick: handleRowClick,
      onPageChange: handlePageChange,
      onSortChange: handleSortChange,
      onFilterChange: filterChange
    }, _createSlots({
      default: _withCtx(() => [
        (props.showRootFarm)
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 0,
              label: _ctx.$t('misc.park'),
              "min-width": 190,
              sortable: "custom",
              prop: "order[rootDevice.deviceGroup.name]"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(scope.row?.deviceGroup ? scope.row?.deviceGroup : '-'), 1)
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (props.showRootDevice)
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 1,
              label: _ctx.$t('misc.turbine'),
              "min-width": 200,
              sortable: "custom",
              prop: "order_device_name"
            }, {
              default: _withCtx((scope) => [
                _createVNode(_unref(NkGroup), {
                  spacing: 2,
                  "no-wrap": "",
                  title: scope.row.device?.name || '-'
                }, {
                  default: _withCtx(() => [
                    (scope.row.device?.name)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          (scope.row.device?.svg)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: "inspection-table__device-icon",
                                innerHTML: scope.row.device?.svg
                              }, null, 8, _hoisted_1))
                            : _createCommentVNode("", true),
                          _createElementVNode("span", null, _toDisplayString(_unref(resume)(scope.row.device?.name, 25)), 1)
                        ], 64))
                      : (_openBlock(), _createElementBlock("span", _hoisted_2, "-"))
                  ]),
                  _: 2
                }, 1032, ["title"])
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(NkTableColumn), {
          label: _ctx.$t('misc.type'),
          "column-key": 'type',
          "min-width": 110,
          filters: [
          { text: 'Clarity', value: '2'},
          { text: 'Sherlock Standard', value: '4' },
          { text: 'Sherlock Plus', value: '3' },
          { text: 'Sherlock Motion', value: '7' },
          { text: 'Volta', value: '1' },
          { text: 'Volta Certified', value: '9' },
          { text: 'Gap Nacelle', value: '6' },
          { text: 'Gap Nacelle Certified', value: '10' },
          { text: 'Shunt', value: '11' },
          { text: 'Shunt Certified', value: '12' },
          { text: 'Volta Plus', value: '13' },
          { text: 'Volta Plus Certified', value: '14' },
          { text: 'Gap Nacelle Plus', value: '15' },
          { text: 'Gap Nacelle Plus Certified', value: '16' },
          { text: 'Shunt Plus', value: '17' },
          { text: 'Shunt Plus Certified', value: '18' },
          { text: 'Volta Express', value: '8' },
        ]
        }, {
          default: _withCtx((scope) => [
            (scope.row.service?.name && scope.row.service?.svg)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  title: scope.row.service?.name,
                  class: "inspection-table__service-icon",
                  innerHTML: scope.row.service?.svg
                }, null, 8, _hoisted_3))
              : (_openBlock(), _createElementBlock("span", _hoisted_4, "-"))
          ]),
          _: 1
        }, 8, ["label"]),
        _createVNode(_unref(NkTableColumn), {
          "min-width": 150,
          label: _ctx.$t('misc.date'),
          sortable: "custom",
          prop: "order[date]"
        }, {
          default: _withCtx((scope) => [
            _createTextVNode(_toDisplayString(_unref(formatDate)(scope.row.date)), 1)
          ]),
          _: 1
        }, 8, ["label"]),
        _createVNode(_unref(NkTableColumn), {
          label: _ctx.$t('misc.review'),
          "min-width": 300,
          "column-key": 'review',
          filters: [
          { text: 'Critical', value: '5' },
          { text: 'High Severity', value: '4' },
          { text: 'Middle Severity', value: '3' },
          { text: 'Low Severity', value: '2' },
          { text: 'Information', value: '1' },
          { text: 'Not Specified', value: '0' },
        ]
        }, {
          default: _withCtx((scope) => [
            (scope.row.severityCounter)
              ? (_openBlock(), _createBlock(_unref(NkGroup), {
                  key: 0,
                  position: "center",
                  spacing: 4
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.keys(scope.row.severityCounter).reverse(), (severity) => {
                      return (_openBlock(), _createBlock(NkColorSwatch, {
                        key: severity,
                        type: getColorFromSeverity(severity),
                        visible: scope.row.severityCounter[severity] > 0
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(scope.row.severityCounter[severity] ?? 0), 1)
                        ]),
                        _: 2
                      }, 1032, ["type", "visible"]))
                    }), 128))
                  ]),
                  _: 2
                }, 1024))
              : (_openBlock(), _createElementBlock("span", _hoisted_5, " - "))
          ]),
          _: 1
        }, 8, ["label"]),
        (_ctx.$store.getters['connection/isGranted'](['plus_sherlock_report_export','sherlock_report_export','volta_report_export', 'clarity_report_export']))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 2,
              width: 100,
              label: _ctx.$t('misc.report')
            }, {
              default: _withCtx(({row}) => [
                _createVNode(_unref(NkButton), {
                  onClick: _withModifiers(() => handlePDFModalOpen(row.id), ["prevent","stop"]),
                  title: _ctx.$t('inspection-group.report.allowed'),
                  class: "text-black",
                  icon: "NkDownload"
                }, null, 8, ["onClick", "title"])
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true),
        (_ctx.$store.getters['connection/isGranted'](['plus_sherlock_report_export','sherlock_report_export','volta_report_export','clarity_report_export']))
          ? (_openBlock(), _createBlock(_unref(NkTableColumn), {
              key: 3,
              width: 100,
              label: _ctx.$t('misc.zip')
            }, {
              default: _withCtx(({row}) => [
                _createVNode(_unref(NkButton), {
                  onClick: _withModifiers(() => handleCSVModalOpen(row.id), ["prevent","stop"]),
                  disabled: row.severityCounter?.length > 0 && !row.severityCounter.some(e => e !== '0'),
                  class: "text-black",
                  icon: "NkDownload"
                }, null, 8, ["onClick", "disabled"])
              ]),
              _: 1
            }, 8, ["label"]))
          : _createCommentVNode("", true)
      ]),
      _: 2
    }, [
      (__props.itemsPerPageSelection)
        ? {
            name: "footer-end",
            fn: _withCtx(() => [
              _createVNode(_unref(NkSelect), {
                modelValue: maxPerPage.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((maxPerPage).value = $event)),
                label: _ctx.$t('misc.items_per_page'),
                class: "pt-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkOption), {
                    value: 5,
                    label: "5"
                  }),
                  _createVNode(_unref(NkOption), {
                    value: 10,
                    label: "10"
                  }),
                  _createVNode(_unref(NkOption), {
                    value: 20,
                    label: "20"
                  }),
                  _createVNode(_unref(NkOption), {
                    value: 50,
                    label: "50"
                  }),
                  _createVNode(_unref(NkOption), {
                    value: 100,
                    label: "100"
                  })
                ]),
                _: 1
              }, 8, ["modelValue", "label"])
            ])
          }
        : undefined
    ]), 1032, ["data", "total-items", "max-per-page", "current-page"]),
    _createVNode(_unref(NkInlineModal), {
      ref_key: "PDFModal",
      ref: PDFModal,
      title: _ctx.$t('inspection-group.action.pdf_report'),
      class: "max-w-3xl",
      onClose: handlePDFModalClose
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(NkGroup), {
          spacing: 4,
          "no-wrap": "",
          align: "flex-start"
        }, {
          default: _withCtx(() => [
            (PDFModalId.value)
              ? (_openBlock(), _createBlock(NkInspectionPDFForm, {
                  key: 0,
                  inspectionGroup: PDFModalId.value,
                  class: "basis-1/2"
                }, null, 8, ["inspectionGroup"]))
              : _createCommentVNode("", true),
            (PDFModalId.value)
              ? (_openBlock(), _createBlock(NkInspectionPDFDownload, {
                  key: 1,
                  inspectionGroup: PDFModalId.value,
                  class: "basis-1/2"
                }, null, 8, ["inspectionGroup"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_unref(NkInlineModal), {
      ref_key: "CSVModal",
      ref: CSVModal,
      title: _ctx.$t('inspection-group.action.export_in_zip'),
      class: "max-w-sm",
      onClose: handleCSVModalClose
    }, {
      content: _withCtx(() => [
        (CSVModalId.value)
          ? (_openBlock(), _createBlock(InspectionZIPForm, {
              key: 0,
              inspectionGroup: CSVModalId.value
            }, null, 8, ["inspectionGroup"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["title"])
  ])), [
    [_directive_loading, _unref(isLoading) || _unref(isRefetching)]
  ])
}
}

})